import { createContext, useContext, ReactNode, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  workdayStart: string;
  workdayEnd: string;
  role?: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  scheduledStartDate?: Date;
  scheduledEndDate?: Date;
  assignedTo?: string;
  assignedStaff?: any[];
  source?: string;
  highestConflictSeverity?: string;
  [key: string]: any;
}

interface StaffEventsContextValue {
  staff: StaffMember[];
  events: CalendarEvent[];
  isLoading: boolean;
}

const StaffEventsContext = createContext<StaffEventsContextValue | undefined>(undefined);

interface StaffEventsProviderProps {
  children: ReactNode;
}

export function StaffEventsProvider({ children }: StaffEventsProviderProps) {
  // Query for staff members
  const { data: staffData = [], isLoading: staffLoading } = useQuery<StaffMember[]>({
    queryKey: ['/users'],
  });

  // Query for calendar events (jobs)
  const { data: eventsData = [], isLoading: eventsLoading } = useQuery<CalendarEvent[]>({
    queryKey: ['/jobs'],
  });

  // Populate window globals for backward compatibility
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).WORKDECK_STAFF = staffData;
      (window as any).WORKDECK_EVENTS = eventsData;
    }
  }, [staffData, eventsData]);

  const value: StaffEventsContextValue = {
    staff: staffData,
    events: eventsData,
    isLoading: staffLoading || eventsLoading,
  };

  return (
    <StaffEventsContext.Provider value={value}>
      {children}
    </StaffEventsContext.Provider>
  );
}

export function useStaffEventsData(): StaffEventsContextValue {
  const context = useContext(StaffEventsContext);
  if (context === undefined) {
    // Fallback to window globals if context not available
    if (typeof window !== 'undefined') {
      return {
        staff: (window as any).WORKDECK_STAFF || [],
        events: (window as any).WORKDECK_EVENTS || [],
        isLoading: false,
      };
    }
    throw new Error('useStaffEventsData must be used within StaffEventsProvider or have window globals available');
  }
  return context;
}
